import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { useMutationCallbacks } from '@woovi/relay';
import { CHECKOUT_TYPE_ENUM } from '@woovi/shared';
import { PaymentFooter, ActionButton } from '@woovi/ui';

import type { PaymentCheckout_checkout$key } from './__generated__/PaymentCheckout_checkout.graphql.ts';
import { CheckoutChargeAdd } from './mutations/CheckoutChargeAddMutation.tsx';
import PaymentCheckoutCompanyData from './PaymentCheckoutCompanyData.tsx';
import PaymentCheckoutDeactivated from './PaymentCheckoutDeactivated.tsx';
import PaymentCheckoutHeading from './PaymentCheckoutHeading.tsx';
import PaymerntCheckoutMethods from './PaymentCheckoutMethods.tsx';
import PaymentCheckoutPixAutomatic from './PaymentCheckoutPixAutomatic.tsx';
import PaymentCheckoutTitle from './PaymentCheckoutTitle.tsx';
import PaymentCompanyLogo from '../logo/PaymentCompanyLogo.tsx';
import PaymentCompanySupport from '../support/PaymentCompanySupport.tsx';

type PaymentCheckoutProps = {
  checkout: PaymentCheckout_checkout$key;
};

const PaymentCheckout = (props: PaymentCheckoutProps): ReactNode => {
  const { t } = useTranslation();

  const checkout = useFragment<PaymentCheckout_checkout$key>(
    graphql`
      fragment PaymentCheckout_checkout on Checkout {
        id
        active
        type
        company {
          projectType
          ...PaymentCompanyLogo_company
          ...PaymentCheckoutCompanyData_company
          ...PaymentCompanySupport_company
        }
        ...PaymentCheckoutHeading_checkout
        ...PaymentCheckoutTitle_checkout
        ...PaymentCheckoutMethods_checkout
        ...PaymentCheckoutDeactivated_checkout
        ...PaymentCheckoutPixAutomatic_checkout
      }
    `,
    props.checkout,
  );

  const [checkoutChargeAdd, isPending] = useMutationCallbacks({
    name: 'CheckoutChargeAdd',
    mutation: CheckoutChargeAdd,
    afterCompleted: (response) => {
      const charge = response?.CheckoutChargeAdd?.charge;

      if (charge) {
        window.open(charge.paymentLinkUrl, '_self', 'noopener noreferrer');
      }
    },
  });

  if (checkout.type === CHECKOUT_TYPE_ENUM.PIX_AUTOMATIC_CHECKOUT) {
    return <PaymentCheckoutPixAutomatic checkout={checkout} />;
  }

  if (!checkout.active) {
    return <PaymentCheckoutDeactivated checkout={checkout} />;
  }

  const handleCreateCharge = () => {
    checkoutChargeAdd({
      variables: {
        input: {
          checkout: checkout.id,
        },
      },
    });
  };

  return (
    <Stack spacing={3}>
      <PaymentCompanyLogo company={checkout.company} />
      <PaymentCheckoutHeading checkout={checkout} />
      <PaymentCheckoutTitle checkout={checkout} />
      <PaymerntCheckoutMethods checkout={checkout} />
      <ActionButton
        disabled={isPending}
        loading={isPending}
        onClick={handleCreateCharge}
      >
        {t('Pay')}
      </ActionButton>
      <PaymentCheckoutCompanyData company={checkout.company} />
      <PaymentCompanySupport company={checkout.company} />
      <PaymentFooter projectType={checkout.company?.projectType} />
    </Stack>
  );
};

export default PaymentCheckout;
